import { Control210, Control211, ControlTags } from "aic";
import { ClinicalCodeListReadDto } from "functions/api/library/interface";
import { useCallback, useMemo } from "react";
import { Form } from "react-bootstrap";
import { ComparatorType } from "./interface";
import { AnnotationControlsState } from "./useAnnotationContextWindow";

/**
 * Props for the TerminologySelector component.
 */
interface TerminologySelectorProps {
  formState: AnnotationControlsState;
  codeLists: ClinicalCodeListReadDto[];
  comparatorTypes: ComparatorType[];
  updateFormField: (field: keyof AnnotationControlsState, value: string) => void;
  disabled?: boolean;
}

/**
 * Select comparator and value inputs based on mapped terminology for the chosen variable.
 *
 * @param props - Destructured parameter object
 * @param props.formState - Current annotation form state
 * @param props.codeLists - Available code lists
 * @param props.comparatorTypes - Available comparator types
 * @param props.updateFormField - Handler to update a form field
 * @param props.disabled - Optional disabled flag
 */
export const TerminologySelector = ({
  formState,
  codeLists,
  comparatorTypes,
  updateFormField,
  disabled = false,
}: TerminologySelectorProps): React.ReactElement => {
  const codes = useMemo(() => {
    if (!formState.selectedTerminology) return [];
    const codeList = codeLists.find((cl) => cl.name === formState.selectedTerminology);
    return codeList?.clinicalCodes ?? [];
  }, [codeLists, formState.selectedTerminology]);

  const termOptions = useMemo(() => {
    const mappedCodeList = formState.selectedVariable?.clinicalCodeListId
      ? codeLists.find((cl) => cl.id === formState.selectedVariable!.clinicalCodeListId)
      : undefined;

    if (!mappedCodeList) return [];

    return [
      { value: "", label: "Select term..." },
      ...codes.map((code) => ({
        value: code.codedValue,
        label: `${code.codedValue} - ${code.decodeTranslatedText ?? "No description"}`,
      })),
    ];
  }, [formState.selectedVariable, codeLists, codes]);

  const handleComparatorChange = useCallback(
    (comparatorName: string) => {
      updateFormField("comparator", comparatorName);
    },
    [updateFormField],
  );

  const handleTermChange = useCallback(
    (codedValue: string) => {
      updateFormField("value", codedValue);
    },
    [updateFormField],
  );

  const handleValueEntry = useCallback(
    (value: string) => {
      updateFormField("value", value);
    },
    [updateFormField],
  );

  const handleGroupValueChange = useCallback(
    (selectedValues: string[]) => {
      const codedValues = selectedValues
        .map((displayValue) => {
          const matchingCode = codes.find(
            (code) =>
              `${code.codedValue} - ${code.decodeTranslatedText ?? "No description"}` ===
              displayValue,
          );
          // istanbul ignore next
          return matchingCode?.codedValue ?? displayValue;
        })
        .filter((v) => v !== "");

      updateFormField("value", codedValues.join("\n"));
    },
    [updateFormField, codes],
  );

  const handleGroupTextEntry = useCallback(
    (value: string) => {
      updateFormField("value", value);
    },
    [updateFormField],
  );

  const comparatorOptions = comparatorTypes.map((comparator) => ({
    value: comparator.name,
    label: comparator.denotation,
  }));

  const selectedComparatorType = comparatorTypes.find((c) => c.name === formState.comparator);
  const rhsType = selectedComparatorType?.rhsType ?? "None";

  const currentGroupValues = useMemo(() => {
    if (rhsType !== "Group" || !formState.value) return [];
    const storedValues = formState.value
      .split("\n")
      .map((v) => v.trim())
      .filter((v) => v !== "");

    return storedValues.map((codedValue) => {
      const code = codes.find((c) => c.codedValue === codedValue);
      return code
        ? `${code.codedValue} - ${code.decodeTranslatedText ?? "No description"}`
        : codedValue;
    });
  }, [rhsType, formState.value, codes]);

  const groupTermOptions = useMemo(() => {
    return codes.map(
      (code) => `${code.codedValue} - ${code.decodeTranslatedText ?? "No description"}`,
    );
  }, [codes]);

  return (
    <>
      <Control211
        id="comparator-select"
        label="Comparator"
        labelWidth={3}
        value={formState.comparator ?? ""}
        disabled={disabled || !formState.selectedVariable}
        options={[{ value: "", label: "None" }, ...comparatorOptions]}
        setValue={handleComparatorChange}
      />
      {formState.selectedTerminology && formState.comparator && rhsType !== "None" && (
        <Control210
          id="mapped-code-list"
          label="Terminology"
          labelWidth={3}
          value={`${formState.selectedTerminology}`}
          disabled={disabled}
        />
      )}
      {formState.comparator && rhsType !== "None" && (
        <>
          {rhsType === "Group" ? (
            formState.selectedTerminology ? (
              <Form.Group className="row mb-2">
                <Form.Label
                  column
                  sm={3}
                  htmlFor="group-value-tags"
                >
                  Values
                </Form.Label>
                <div className="col-sm-9">
                  <ControlTags
                    availableOptions={groupTermOptions}
                    selectedTags={currentGroupValues}
                    onSelectionChange={handleGroupValueChange}
                    placeholder="Select values..."
                    disabled={disabled || !formState.selectedTerminology}
                  />
                </div>
              </Form.Group>
            ) : (
              <Control210
                id="group-value-textarea"
                label="Values"
                labelWidth={3}
                value={formState.value ?? ""}
                setValue={handleGroupTextEntry}
                type="textarea"
                rows={4}
                allowCarriageReturn={true}
                disabled={disabled || !formState.selectedVariable}
                subText="Enter each value on a new line (press Enter between values)"
              />
            )
          ) : formState.selectedTerminology ? (
            <Control211
              id="term-select"
              label="Value"
              labelWidth={3}
              value={formState.value ?? ""}
              disabled={disabled || !formState.selectedTerminology}
              options={termOptions}
              setValue={handleTermChange}
            />
          ) : (
            <Control210
              id="value-text"
              label="Value"
              labelWidth={3}
              value={formState.value ?? ""}
              setValue={handleValueEntry}
              disabled={disabled || !formState.selectedVariable}
            />
          )}
        </>
      )}
    </>
  );
};

TerminologySelector.displayName = "TerminologySelector";
